import React, { useState } from "react";

// A 12-point trend for a stat tile: the line sits in a de-emphasised tone and
// only the current period carries the accent, so the tile's number stays the
// loudest thing in the card.
//
// Accent is #22c55e on light and #16a34a on dark. Both were run through the
// palette validator against their own surface; the lighter step fails the dark
// lightness band, which is why the two modes use different steps rather than
// one colour flipped.
const VIEW_W = 240;
const VIEW_H = 48;
const PAD_Y = 4; // keeps the 2px stroke from clipping at the extremes

const Sparkline = ({ points, valueLabel = "total", className = "" }) => {
  const [hover, setHover] = useState(null);

  if (!points || points.length < 2) return null;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1; // a flat series would divide by zero

  const xOf = (i) => (i / (points.length - 1)) * VIEW_W;
  const yOf = (v) => PAD_Y + (1 - (v - min) / span) * (VIEW_H - PAD_Y * 2);

  const line = points.map((p, i) => `${xOf(i)},${yOf(p.value)}`).join(" ");
  const area = `${line} ${VIEW_W},${VIEW_H} 0,${VIEW_H}`;

  const last = points[points.length - 1];
  const active = hover === null ? points.length - 1 : hover;
  const activePoint = points[active];

  // Percentages so the marker stays circular while the SVG itself stretches.
  const leftPct = (xOf(active) / VIEW_W) * 100;
  const topPct = (yOf(activePoint.value) / VIEW_H) * 100;

  return (
    <div
      className={`relative text-[#22c55e] dark:text-[#16a34a] ${className}`}
      role="img"
      aria-label={`${valueLabel} trend: ${points[0].value} in ${points[0].label} rising to ${last.value} in ${last.label}`}
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="none"
        className="h-12 w-full"
        aria-hidden="true"
      >
        <polygon points={area} fill="currentColor" opacity="0.12" />
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.45"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Current period marker, in full accent against the de-emphasised line. */}
      <span
        className="pointer-events-none absolute h-2.5 w-2.5 rounded-full border-2 border-white bg-current dark:border-darkblack-600"
        style={{
          left: `${leftPct}%`,
          top: `${topPct}%`,
          transform: "translate(-50%, -50%)",
        }}
      />

      {/* Hit targets are a full column each, far bigger than the mark itself. */}
      <div className="absolute inset-0 flex" onMouseLeave={() => setHover(null)}>
        {points.map((p, i) => (
          <div
            key={p.label}
            className="h-full flex-1"
            onMouseEnter={() => setHover(i)}
          />
        ))}
      </div>

      {hover !== null && (
        <div
          className="pointer-events-none absolute bottom-full z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-bgray-900 px-2 py-1 text-xs font-medium text-white"
          // The marker keeps its true position; only the tooltip is pulled
          // inward so it cannot hang off the edge of the card.
          style={{ left: `${Math.min(85, Math.max(15, leftPct))}%` }}
        >
          {activePoint.label}: {activePoint.value.toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default Sparkline;
